import { Tooltip } from '@wegroup/design-system';
import cogoToast from 'cogo-toast';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import LargeButton from '../../../../common/src/components/LargeButton';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import { useQuotes } from '../../hooks/useQuoteQueries';
import { RootState } from '../../store/redux/reducers/rootReducer';
import { getOfferState, OfferState } from '../../services/offersService';
import { areAllAcknowledgementsAnsweredSelector } from '../../store/redux/slices/offersSlice';
import {
  CampaignExtend,
  getCampaignExtend,
} from '../../store/redux/slices/productFactorySlice';
import useTracking from '../../../../common/src/hooks/tracking/useTracking';
import useAuth from '../../../../app/src/features/authentication/hooks/useAuth';

const RequestTheseOffersButton: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { trackEvent } = useTracking();
  const sessionId = useSessionId();

  const areAllAcknowledgementsAnswered = useSelector((state: RootState) =>
    areAllAcknowledgementsAnsweredSelector(state),
  );
  const preferredOffers = useSelector(
    (state: RootState) => state.offers.preferredOffers,
  );
  const selectedOffers = useSelector(
    (state: RootState) => state.offers.selectedOffers,
  );
  const campaignExtend = useSelector((state: RootState) =>
    getCampaignExtend(state),
  );

  const { campaign } = useAuth();
  const showAcknowledgements = campaign?.show_acknowledgements;

  const hasPreferredOffer = Object.keys(selectedOffers).every(
    (riskObject) => !!preferredOffers[riskObject],
  );

  const { data: quotes, isFetched } = useQuotes(selectedOffers);

  const isAnySelectedOfferFailed = quotes.some(
    (quote) => getOfferState(quote) === OfferState.ERROR,
  );

  const getErrorText = () => {
    if (!isFetched)
      return t(`Please wait for your offers to finish calculating`);
    if (isAnySelectedOfferFailed)
      return t(
        `You have selected an offer that failed to calculate Please remove this offer to continue`,
      );
    if (!hasPreferredOffer) return t(`Please select a preferred offer`);
    if (!areAllAcknowledgementsAnswered)
      return t(`Please answer all questions`);
  };

  const handleDisabledClick = () => {
    const errorText = getErrorText();
    if (errorText) {
      cogoToast.warn(errorText, { hideAfter: 7 });

      if (isAnySelectedOfferFailed || !isFetched) {
        handleScrollToTop();
      }
      if (!areAllAcknowledgementsAnswered) {
        handleScrollToQuestions();
      }
    }
  };

  const handleScrollToTop = () => {
    const scrollContainer = document.getElementById(
      'SelectedOffersMainContainer',
    );

    scrollContainer?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleScrollToQuestions = () => {
    const element = document.getElementById(
      'AcknowledgementContainer',
    ) as HTMLElement;
    const scrollContainer = document.getElementById(
      'SelectedOffersMainContainer',
    );

    scrollContainer?.scrollTo({
      top: element && element.offsetTop - 30,
      behavior: 'smooth',
    });
  };

  const canOffersBeMade = (() => {
    if (isAnySelectedOfferFailed || !isFetched) return false;
    if ((areAllAcknowledgementsAnswered && hasPreferredOffer) || showAcknowledgements === false) return true;
    return false;
  })();

  const handleOnRequest = () => {
    trackEvent('Request offers in Sales Conversation');
    if (campaignExtend === CampaignExtend.INTERNAL_OFFER) {
      history.push(`/product/session/finalize-offers?session_id=${sessionId}`);
    } else {
      history.push(`/product/session/sign?session_id=${sessionId}`);
    }
  };

  return (
    <Tooltip label={getErrorText()}>
      <LargeButton
        disabled={!canOffersBeMade}
        onDisabledClick={handleDisabledClick}
        onClick={handleOnRequest}
        dataTestId="ADVISORY-CONVO_OFFERS_SELECTED-OFFERS_REQUEST-THESE-OFFERS-BUTTON"
      >
        {t('Request these offers')}
      </LargeButton>
    </Tooltip>
  );
};

export default RequestTheseOffersButton;
